import { useState, type FormEvent } from 'react';
import { Building2, User, Mail, Phone, Globe, MapPin, FileText } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';

interface AddProspectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (prospect: NewProspect) => void;
}

export interface NewProspect {
  companyName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  contactTitle: string;
  website: string;
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  source: string;
  notes: string;
}

const SOURCES = [
  'Website',
  'Referral',
  'Trade Show',
  'Cold Call',
  'LinkedIn',
  'Email Campaign',
  'Partner',
  'Other',
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

export function AddProspectModal({ isOpen, onClose, onSubmit }: AddProspectModalProps) {
  const [formData, setFormData] = useState<NewProspect>({
    companyName: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    contactTitle: '',
    website: '',
    address: {
      street: '',
      city: '',
      state: '',
      zip: '',
    },
    source: '',
    notes: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof NewProspect, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof NewProspect, string>> = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }
    if (!formData.contactName.trim()) {
      newErrors.contactName = 'Contact name is required';
    }
    if (!formData.contactEmail.trim()) {
      newErrors.contactEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Invalid email format';
    }
    if (!formData.source) {
      newErrors.source = 'Please select a source';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500));
    
    onSubmit(formData);
    
    // Reset form
    setFormData({
      companyName: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      contactTitle: '',
      website: '',
      address: {
        street: '',
        city: '',
        state: '',
        zip: '',
      },
      source: '',
      notes: '',
    });
    setErrors({});
    setIsSubmitting(false);
    onClose();
  };

  const updateField = (field: keyof NewProspect, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const updateAddress = (field: keyof NewProspect['address'], value: string) => {
    setFormData(prev => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Prospect" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Company Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-slate dark:text-mist flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Company Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Company Name <span className="text-sunset">*</span>
              </label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => updateField('companyName', e.target.value)}
                placeholder="Acme Bank"
                className={`input ${errors.companyName ? 'border-sunset' : ''}`}
              />
              {errors.companyName && (
                <p className="mt-1 text-sm text-sunset">{errors.companyName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Website
              </label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-french-gray" />
                <input
                  type="text"
                  value={formData.website}
                  onChange={(e) => updateField('website', e.target.value)}
                  placeholder="www.acmebank.com"
                  className="input pl-10"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Source <span className="text-sunset">*</span>
              </label>
              <select
                value={formData.source}
                onChange={(e) => updateField('source', e.target.value)}
                className={`select ${errors.source ? 'border-sunset' : ''}`}
              >
                <option value="">Select source...</option>
                {SOURCES.map(source => (
                  <option key={source} value={source}>{source}</option>
                ))}
              </select>
              {errors.source && (
                <p className="mt-1 text-sm text-sunset">{errors.source}</p>
              )}
            </div>
          </div>
        </div>

        {/* Primary Contact */}
        <div className="space-y-4 pt-4 border-t border-gray dark:border-night-pond">
          <h3 className="text-sm font-medium text-slate dark:text-mist flex items-center gap-2">
            <User className="w-4 h-4" />
            Primary Contact
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Contact Name <span className="text-sunset">*</span>
              </label>
              <input
                type="text"
                value={formData.contactName}
                onChange={(e) => updateField('contactName', e.target.value)}
                placeholder="John Smith"
                className={`input ${errors.contactName ? 'border-sunset' : ''}`}
              />
              {errors.contactName && (
                <p className="mt-1 text-sm text-sunset">{errors.contactName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Title
              </label>
              <input
                type="text"
                value={formData.contactTitle}
                onChange={(e) => updateField('contactTitle', e.target.value)}
                placeholder="VP of Operations"
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Email <span className="text-sunset">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-french-gray" />
                <input
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => updateField('contactEmail', e.target.value)}
                  placeholder="jsmith@acmebank.com"
                  className={`input pl-10 ${errors.contactEmail ? 'border-sunset' : ''}`}
                />
              </div>
              {errors.contactEmail && (
                <p className="mt-1 text-sm text-sunset">{errors.contactEmail}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Phone
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-french-gray" />
                <input
                  type="tel"
                  value={formData.contactPhone}
                  onChange={(e) => updateField('contactPhone', e.target.value)}
                  placeholder="(555) 555-5555"
                  className="input pl-10"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="space-y-4 pt-4 border-t border-gray dark:border-night-pond">
          <h3 className="text-sm font-medium text-slate dark:text-mist flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Address
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                Street Address
              </label>
              <input
                type="text"
                value={formData.address.street}
                onChange={(e) => updateAddress('street', e.target.value)}
                placeholder="123 Main Street"
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                City
              </label>
              <input
                type="text"
                value={formData.address.city}
                onChange={(e) => updateAddress('city', e.target.value)}
                placeholder="Chicago"
                className="input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                  State
                </label>
                <select
                  value={formData.address.state}
                  onChange={(e) => updateAddress('state', e.target.value)}
                  className="select"
                >
                  <option value="">Select...</option>
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
                  ZIP Code
                </label>
                <input
                  type="text"
                  value={formData.address.zip}
                  onChange={(e) => updateAddress('zip', e.target.value)}
                  placeholder="60601"
                  className="input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="space-y-4 pt-4 border-t border-gray dark:border-night-pond">
          <h3 className="text-sm font-medium text-slate dark:text-mist flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Notes
          </h3>
          
          <div>
            <textarea
              value={formData.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              placeholder="Add any additional notes about this prospect..."
              rows={3}
              className="input resize-none"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray dark:border-night-pond">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={isSubmitting}>
            <Building2 className="w-4 h-4" />
            {isSubmitting ? 'Adding...' : 'Add Prospect'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
